import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FileSpreadsheet, UploadCloud, Loader2, Download, CheckCircle2, AlertTriangle,
  ArrowRight, Trash2, Database, RotateCcw,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo, titleCase } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader } from "@/components/app/Loader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const SKIP = "__skip__";

export default function VoterFile() {
  const qc = useQueryClient();
  const fileInput = useRef(null);
  const [step, setStep] = useState("idle"); // idle | mapping | processing | done
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("incremental");
  const [activeId, setActiveId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attested, setAttested] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dragging, setDragging] = useState(false);

  const { data: imports } = useQuery({ queryKey: ["imports"], queryFn: () => api.get("/voters/imports").then((r) => r.data) });
  const { data: stats } = useQuery({ queryKey: ["voterStats"], queryFn: () => api.get("/voters/stats").then((r) => r.data) });

  const importStatus = useQuery({
    queryKey: ["import", activeId],
    queryFn: () => api.get(`/voters/imports/${activeId}`).then((r) => r.data),
    enabled: step === "processing" && !!activeId,
    refetchInterval: 1500,
  });

  useEffect(() => {
    const d = importStatus.data;
    if (step === "processing" && d && (d.status === "completed" || d.status === "failed")) {
      setStep("done");
      setUpload(d);
      qc.invalidateQueries({ queryKey: ["imports"] });
      qc.invalidateQueries({ queryKey: ["voterStats"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importStatus.data, step]);

  const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB, bypasses proxy body limits for statewide files

  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      let data;
      if (file.size > CHUNK_SIZE) {
        const init = await api.post("/voters/upload/init", { filename: file.name });
        const uploadId = init.data.upload_id;
        const total = Math.ceil(file.size / CHUNK_SIZE);
        for (let i = 0; i < total; i++) {
          const fd = new FormData();
          fd.append("upload_id", uploadId);
          fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
          await api.post("/voters/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setUploadPct(Math.round(((i + 1) / total) * 100));
        }
        data = (await api.post("/voters/upload/complete", { upload_id: uploadId })).data;
      } else {
        const fd = new FormData();
        fd.append("file", file);
        data = (await api.post("/voters/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      }
      setUpload(data);
      setMapping(data.suggested_mapping || {});
      setStep("mapping");
      if (data.had_saved_mapping) toast.success("Recognized this format. Mapping pre-filled");
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setBusy(false);
      setUploadPct(0);
    }
  };

  const commit = async () => {
    if (!attested) return toast.error("Confirm the file was lawfully obtained before importing");
    setBusy(true);
    try {
      const { data } = await api.post(`/voters/imports/${upload.import_id}/commit`, { mapping, mode, attestation: true });
      setActiveId(data.import_id);
      setStep("processing");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setStep("idle"); setUpload(null); setMapping({}); setActiveId(null); setAttested(false); };

  const download = async (url, name) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href; a.download = name; a.click(); URL.revokeObjectURL(href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const purge = async (id) => {
    if (!window.confirm("Remove all voters from this import?")) return;
    try {
      await api.delete(`/voters/imports/${id}`);
      toast.success("Import purged");
      qc.invalidateQueries({ queryKey: ["imports"] });
      qc.invalidateQueries({ queryKey: ["voterStats"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Voter File"
        subtitle="Upload your VAN / QVF / vendor export. We map columns, validate, and import in the background, which powers personas, segments, and the sentiment map."
        icon={FileSpreadsheet}
        actions={
          <Button variant="outline" onClick={() => download("/voters/sample-file", "sample_voter_file.csv")} data-testid="download-sample-btn">
            <Download className="mr-2 h-4 w-4" /> Sample file
          </Button>
        }
      />

      {stats && (
        <div className="mb-6 flex flex-wrap gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 shadow-sm">
            <Database className="h-4 w-4 text-brand-navy" />
            <span className="font-heading text-lg font-bold text-brand-ink">{stats.total.toLocaleString()}</span>
            <span className="text-sm text-brand-mute">voters in universe</span>
          </div>
        </div>
      )}

      {/* STEP: idle (drop zone) */}
      {step === "idle" && (
        <div
          data-testid="upload-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files?.[0]); }}
          onClick={() => fileInput.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 text-center transition-colors ${dragging ? "border-brand-red bg-brand-red/5" : "border-border bg-white hover:border-brand-red"}`}
        >
          <input ref={fileInput} type="file" accept=".csv,.xlsx,.xls" data-testid="upload-voter-file-input" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
          {busy ? <Loader2 className="h-8 w-8 animate-spin text-brand-red" /> : <UploadCloud className="h-10 w-10 text-brand-red" />}
          <h3 className="mt-4 font-heading text-lg font-bold text-brand-ink">
            {busy && uploadPct > 0 ? `Uploading… ${uploadPct}%` : "Drop your voter file here"}
          </h3>
          <p className="mt-1 text-sm text-brand-mute">CSV, XLSX or XLS · large files upload in chunks automatically</p>
        </div>
      )}

      {/* STEP: mapping */}
      {step === "mapping" && upload && (
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-heading text-lg font-bold text-brand-ink">Map your columns</h3>
                <p className="text-sm text-brand-mute">{upload.filename} · {upload.row_count.toLocaleString()} rows detected</p>
              </div>
              <Button variant="ghost" onClick={reset}>Cancel</Button>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {upload.canonical_fields.map((cf) => (
                <div key={cf} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                  <Label className="text-sm font-semibold text-brand-ink">{titleCase(cf)}</Label>
                  <Select
                    value={mapping[cf] || SKIP}
                    onValueChange={(v) => setMapping((m) => ({ ...m, [cf]: v === SKIP ? undefined : v }))}
                  >
                    <SelectTrigger className="w-[170px]" data-testid={`map-${cf}`}><SelectValue placeholder="(skip)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SKIP}>(skip)</SelectItem>
                      {upload.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
            <div className="border-b border-border px-5 py-3">
              <h3 className="font-heading text-sm font-bold text-brand-ink">Preview · first {upload.sample_rows.length} rows</h3>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>{upload.headers.map((h) => (
                    <TableHead key={h} className="whitespace-nowrap">
                      {h}<br />
                      <span className="text-[0.6rem] font-normal text-brand-mute">{upload.inferred_types[h]}</span>
                    </TableHead>
                  ))}</TableRow>
                </TableHeader>
                <TableBody>
                  {upload.sample_rows.map((row, i) => (
                    <TableRow key={i}>{upload.headers.map((h) => <TableCell key={h} className="whitespace-nowrap text-xs">{row[h] ?? ""}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-brand-red/20 bg-brand-red/5 p-4" data-testid="attestation-card">
            <Checkbox
              id="attestation"
              checked={attested}
              onCheckedChange={(v) => setAttested(v === true)}
              className="mt-0.5 border-brand-red data-[state=checked]:bg-brand-red data-[state=checked]:text-white"
              data-testid="attestation-checkbox"
            />
            <div>
              <Label htmlFor="attestation" className="cursor-pointer text-sm font-semibold text-brand-ink">
                I confirm this file was lawfully obtained and its use complies with the{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-brand-red underline underline-offset-2">Privacy Policy</a>.
              </Label>
              <p className="mt-1 text-[0.7rem] text-brand-mute">
                Required before an import runs. Your name, the file and a timestamp are stored on the
                import record and in Settings → Compliance.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <Label>Import mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-[200px]" data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incremental">Incremental (upsert)</SelectItem>
                  <SelectItem value="full_replace">Full replace</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="commit-import-btn" onClick={commit} disabled={busy || !attested} className="bg-brand-red text-white hover:bg-brand-redhover">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />} Import {upload.row_count.toLocaleString()} rows
            </Button>
          </div>
        </div>
      )}

      {/* STEP: processing */}
      {step === "processing" && (
        <div className="rounded-xl border border-border bg-white p-10 shadow-sm" data-testid="import-processing">
          <Loader label="Importing in the background worker… this won't block you." />
          {importStatus.data?.total_rows > 0 && (
            <div className="mx-auto mt-6 max-w-md">
              <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-2 rounded-full bg-brand-red transition-all"
                  style={{ width: `${Math.min(100, Math.round(((importStatus.data.processed_rows || 0) / importStatus.data.total_rows) * 100))}%` }}
                />
              </div>
              <p className="mt-2 text-center text-xs text-brand-mute" data-testid="import-progress-text">
                {(importStatus.data.processed_rows || 0).toLocaleString()} / {importStatus.data.total_rows.toLocaleString()} rows processed
              </p>
            </div>
          )}
        </div>
      )}

      {/* STEP: done */}
      {step === "done" && upload && (
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <div className="flex items-center gap-3">
            {upload.status === "completed" ? <CheckCircle2 className="h-7 w-7 text-pos-text" /> : <AlertTriangle className="h-7 w-7 text-brand-red" />}
            <div>
              <h3 className="font-heading text-lg font-bold text-brand-ink">Import {upload.status}</h3>
              <p className="text-sm text-brand-mute">{upload.filename}</p>
            </div>
          </div>
          {upload.failure_reason && (
            <p className="mt-4 rounded-lg border border-brand-red/20 bg-brand-red/5 p-3 text-sm text-brand-red" data-testid="import-failure-reason">
              {upload.failure_reason}
            </p>
          )}
          <div className="mt-5 grid grid-cols-3 gap-3">
            <ResultStat label="Imported" value={upload.imported} accent="green" />
            <ResultStat label="Updated" value={upload.updated} accent="blue" />
            <ResultStat label="Errored" value={upload.errored} accent="red" />
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {upload.errored > 0 && (
              <Button variant="outline" onClick={() => download(`/voters/imports/${upload.id}/errors`, `errors_${upload.id}.csv`)} data-testid="download-errors-btn">
                <Download className="mr-2 h-4 w-4" /> Download error report ({upload.errored})
              </Button>
            )}
            <Button onClick={reset} className="bg-brand-navy text-white hover:bg-brand-navyhover"><RotateCcw className="mr-2 h-4 w-4" /> Import another</Button>
          </div>
        </div>
      )}

      {/* Imports history */}
      {step === "idle" && imports?.length > 0 && (
        <div className="mt-8 overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          <div className="border-b border-border px-5 py-3"><h3 className="font-heading text-sm font-bold text-brand-ink">Import history</h3></div>
          <Table>
            <TableHeader>
              <TableRow><TableHead>File</TableHead><TableHead>Status</TableHead><TableHead>Imported</TableHead><TableHead>Updated</TableHead><TableHead>Errored</TableHead><TableHead>When</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {imports.map((im) => (
                <TableRow key={im.id}>
                  <TableCell className="font-medium text-brand-ink">{im.filename}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{im.status}</Badge>
                    {im.failure_reason && (
                      <p className="mt-1 max-w-[16rem] text-[0.7rem] leading-snug text-brand-red" data-testid={`failure-reason-${im.id}`}>
                        {im.failure_reason}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{im.imported}</TableCell>
                  <TableCell>{im.updated}</TableCell>
                  <TableCell>{im.errored}</TableCell>
                  <TableCell className="text-xs text-brand-mute">{timeAgo(im.created_at)}</TableCell>
                  <TableCell>
                    {im.status !== "purged" && (
                      <Button variant="ghost" size="icon" onClick={() => purge(im.id)} data-testid={`purge-${im.id}`}><Trash2 className="h-4 w-4 text-brand-red" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function ResultStat({ label, value, accent }) {
  const map = { green: "text-pos-text", blue: "text-brand-blue", red: "text-brand-red" };
  return (
    <div className="rounded-lg border border-border p-4 text-center">
      <p className={`font-heading text-3xl font-extrabold ${map[accent]}`}>{value}</p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-brand-mute">{label}</p>
    </div>
  );
}
